import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../contexts/AuthContext";
import { Box, Grid, Paper, Typography, Divider } from "@mui/material";
import VisitsChart from "../VisitsChart/VisitsChart";
import PieChart from "../PieChart/PieChart";
import ConversionRatesChart from "../ConversionRatesChart/ConversionRatesChart";
import IncomeChart from "../IncomeChart/IncomeChart";

function OverviewDashboard() {
  const { userDetails } = useAuth();
  const [stats, setStats] = useState([]);
  const [error, setError] = useState(null);
  const [totalListings, setTotalListings] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [totalOpenHouses, setTotalOpenHouses] = useState(0);
  const [totalReservationCount, setTotalReservationCount] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!userDetails?.id) {
          setError("User details are missing. Unable to fetch pro_id.");
          return;
        }

        const response = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/dash/dashboard-insights/summary?proSlug=${userDetails.id}`
        );

        const data = response.data;
        if (data.success) {
          // console.log(data);
          setTotalListings(data.totalListings || 0);
          setTotalViews(data.totalViews || 0);
          setTotalOpenHouses(data.totalOpenProperties || 0);
          setTotalReservationCount(data.totalReservationCount || 0);
        } else {
          setError(data.message || "Failed to fetch dashboard insights.");
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
        setError("An error occurred while fetching data.");
      }
    };

    fetchStats();
  }, [userDetails]);

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: "red", color: "white" }}>
          <Typography variant="h6">Error</Typography>
          <Typography>{error}</Typography>
        </Paper>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Total Listings</Typography>
            <Typography variant="h4">{totalListings}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Listing Views</Typography>
            <Typography variant="h4">{totalViews}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Open Houses</Typography>
            <Typography variant="h4">{totalOpenHouses}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Reservations</Typography>
            <Typography variant="h4">{totalReservationCount}</Typography>
          </Paper>
        </Grid>

        <Grid container spacing={2}>
          {stats.map(([key, value], index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6">{key}</Typography>
                <Typography variant="h4">{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Add charts below */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Total Sell Listings Value</Typography>
            <VisitsChart userId={userDetails?.id} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Social Media</Typography>
            <PieChart userId={userDetails?.id} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Property Cities</Typography>
            <ConversionRatesChart userId={userDetails?.id} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Social Media Views</Typography>
            <IncomeChart userId={userDetails?.id} />
          </Paper>
        </Grid>
      </Grid>

      {/* Line of Separation */}
      <Divider sx={{ my: 4 }} />

      {/* Explanation Section */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Explanation:
      </Typography>
      <Box sx={{ backgroundColor: "#f9f9f9", p: 3, borderRadius: 2 }}>
        <Typography variant="body1" gutterBottom>
          <strong>Total Listings:</strong> Displays the total number of
          properties currently listed and available on the platform.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Listing Views:</strong> Reflects the total number of views
          across all listed properties.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Open Houses:</strong> Indicates the number of open house
          events that are scheduled.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Total Reservations:</strong> Represents the total count of
          reservation requests received.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Total Sell Listings Value:</strong> Shows the aggregate dollar
          value of all listed properties for sale.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Social Media Engagement:</strong> Provides a breakdown of
          social media activity by category:
        </Typography>
        <Box sx={{ ml: 2 }}>
          <Typography variant="body1">- Yellow: Instagram Reels</Typography>
          <Typography variant="body1">- Green: Instagram Posts</Typography>
          <Typography variant="body1">- Blue: Spotify Podcasts</Typography>
          <Typography variant="body1">- Orange: YouTube Videos</Typography>
        </Box>
        <Typography variant="body1" gutterBottom>
          <strong>Top Property Cities:</strong> Highlights the six cities with
          the highest number of property listings.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Professional Social Media Views:</strong> Summarizes the total
          views of professionals' social media content.
        </Typography>
      </Box>
    </Box>
  );
}

export default OverviewDashboard;
